import type { FunctionComponent } from 'react';

import { isMemberLoggedIn, useMemberId } from '@trello/authentication';
import { isAdminOfEnterprise } from '@trello/business-logic/member';
import { LazyDiscoveryAdPopperContainer } from '@trello/discovery-ads/LazyDiscoveryAdPopperContainer';
import { Entitlements } from '@trello/entitlements';
import { useFeatureGate } from '@trello/feature-gate-client';
import { navigate } from '@trello/router/navigate';
import {
  getOrganizationGuestUrl,
  getOrganizationMembersUrl,
  getOrganizationRequestUrl,
} from '@trello/urls';

import { BoardInviteRestrictValues } from 'app/scripts/views/organization/Constants';
import { InviteWorkspaceMembersButton } from 'app/src/components/TeamProfile/InviteWorkspaceMembersButton';
import { LazyUpgradeSmartComponent } from 'app/src/components/UpgradePrompts';
import { WorkspaceMembersFilterList } from 'app/src/components/WorkspaceMembersFilterList';
import { WorkspaceMembersInfoHeader } from 'app/src/components/WorkspaceMembersInfoHeader';
import { type MemberTypeFilter } from 'app/src/components/WorkspacePages/WorkspaceMembersPage';
import { WorkspacePageHeader } from 'app/src/components/WorkspacePages/WorkspacePageHeader';
import { WorkspacePageSpinner } from 'app/src/components/WorkspacePages/WorkspacePageSpinner';
import { CollaboratorsHeading } from './CollaboratorsHeading';
import { WorkspaceMembersListView } from './WorkspaceMembersListView';
import { useWorkspaceMembersViewQuery } from './WorkspaceMembersViewQuery.generated';

import styles from './WorkspaceMembersView.less';

interface WorkspaceMembersViewProps {
  workspaceId: string;
  isOrganizationAdmin: boolean;
  memberTypeFilter: MemberTypeFilter;
}

export const WorkspaceMembersView: FunctionComponent<
  WorkspaceMembersViewProps
> = ({ workspaceId, isOrganizationAdmin, memberTypeFilter }) => {
  const memberId = useMemberId();
  const { loading, data } = useWorkspaceMembersViewQuery({
    waitOn: ['MemberHeader'],
    variables: {
      memberId,
      organizationId: workspaceId,
    },
  });

  const { value: isM2WorkspaceNavEnabled } = useFeatureGate(
    'billplat_m2_workspace_pages',
  );

  const { organization, member } = data ?? {};
  if (!organization || !member || loading) {
    return <WorkspacePageSpinner />;
  }
  const memberLoggedIn = isMemberLoggedIn();
  const isEnterpriseAdmin = organization?.idEnterprise
    ? isAdminOfEnterprise(member, organization.idEnterprise)
    : false;
  const isAdmin = isEnterpriseAdmin || isOrganizationAdmin;

  const isWorkspaceFree = Entitlements.isFree(organization.offering);
  const hasBC = !(
    isWorkspaceFree || Entitlements.isStandard(organization.offering)
  );

  const orgMembers = organization.members.filter((orgMember) => {
    const membership = organization.memberships.find(
      (m) => m.idMember === orgMember.id,
    );
    return membership && !membership.deactivated && !orgMember.activityBlocked;
  });

  const workspaceMembersAndGuestsCount =
    orgMembers.length + organization.collaborators.length;

  const filtersListProps = {
    memberTypeFilter,
    onMemberTypeChange: (memberType: string) => {
      memberTypeFilter = memberType as MemberTypeFilter;
      if (memberType === 'team-member') {
        return navigate(getOrganizationMembersUrl(organization.name), {});
      } else if (memberType === 'guest') {
        return navigate(getOrganizationGuestUrl(organization.name), {});
      } else if (memberType === 'request') {
        return navigate(getOrganizationRequestUrl(organization.name), {});
      }
    },
    orgMemberCount: orgMembers.length,
    guestMemberCount: organization.collaborators.length,
    requestMemberCount: organization.accessRequests?.length || 0,
    // For showGuestTab, currently this seems to be the only check.
    // Guest tab is always shown if user is logged in. Will know more once we audit members page
    showGuestTab: memberLoggedIn,
    showRequestTab: memberLoggedIn && isAdmin,
    isFetchingCollaborators: loading,
  };

  const infoHeaderProps = {
    canManageMembers: isAdmin,
    guestMemberCount: organization.collaborators.length,
    hasBC,
    isEnterpriseAdmin,
    isOrgManaged: Boolean(
      organization.premiumFeatures?.includes('inviteBoard') &&
        organization.prefs.boardInviteRestrict &&
        [
          BoardInviteRestrictValues.ORG,
          BoardInviteRestrictValues.MANAGED,
          BoardInviteRestrictValues.ORG_OR_MANAGED,
        ].includes(
          organization.prefs.boardInviteRestrict as
            | 'managed'
            | 'org-or-managed'
            | 'org',
        ),
    ),
    isPaidWorkspace: !isWorkspaceFree,
    isStandard: Entitlements.isStandard(organization.offering),
    memberTypeFilter,
    model: {
      belongsToRealEnterprise: () =>
        Entitlements.isEnterprise(organization.offering),
      getProduct: () => organization.products[0],
      get: (key: string) => organization.prefs,
    },
    orgId: workspaceId,
    orgMemberCount: orgMembers.length,
    requestMemberCount: organization.accessRequests?.length || 0,
    orgIsPremiumPO:
      Entitlements.isPremium(organization.offering) &&
      organization.premiumFeatures?.includes('externallyBilled'),
  };

  return (
    <div className={styles.container}>
      <WorkspacePageHeader
        title={
          <div className={styles.title}>
            <CollaboratorsHeading
              collaboratorCount={workspaceMembersAndGuestsCount}
              workspaceId={workspaceId}
              isWorkspaceFree={isWorkspaceFree}
            />
            {isM2WorkspaceNavEnabled && isAdmin && (
              <InviteWorkspaceMembersButton workspaceId={workspaceId} />
            )}
          </div>
        }
      />
      <div className={styles.layout}>
        <div className={styles.sidebar}>
          <WorkspaceMembersFilterList {...filtersListProps} />
          <LazyUpgradeSmartComponent
            orgId={workspaceId}
            promptId="teamMembersScreenPromptFull"
            redirect={false}
          />
        </div>
        <div className={styles.list}>
          <WorkspaceMembersInfoHeader {...infoHeaderProps} />
          <WorkspaceMembersListView
            workspaceId={workspaceId}
            isAdmin={isAdmin}
            memberTypeFilter={memberTypeFilter}
          />
        </div>
      </div>
      {/* Enterprise self-serve experiment */}
      <LazyDiscoveryAdPopperContainer
        source="workspaceMembersScreenBottomRight"
        oneTimeMessage="1p-ads-enterprise-self-serve"
        enterpriseId={organization.idEnterprise?.toString()}
        workspaceId={workspaceId}
      />
    </div>
  );
};
